import { StrictMode, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { ApiError, loadConfig } from './api';
import { App } from './App';
import { beginSignIn, completeSignIn, getTokens, microsoftLogoutUrl } from './auth/session';
import './styles.css';

const root = createRoot(document.getElementById('root')!);

function Message({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="message">
      <h1>{title}</h1>
      {children}
    </div>
  );
}

async function boot(): Promise<void> {
  let config;
  try {
    config = await loadConfig();
  } catch (err) {
    const setup = err instanceof ApiError && err.code === 'NOT_REGISTERED';
    root.render(
      <Message title={setup ? 'Not set up yet' : 'Could not load'}>
        <p>{(err as Error).message}</p>
      </Message>,
    );
    return;
  }

  const path = window.location.pathname;
  if (path === '/signed-out') {
    root.render(
      <Message title="Signed out">
        <p><a href="/">Sign in again</a></p>
        <p className="muted">
          Your Microsoft session is still active. On a shared computer,{' '}
          <a href={microsoftLogoutUrl(config)}>sign out of Microsoft</a> too.
        </p>
      </Message>,
    );
    return;
  }

  if (path === '/auth/callback') {
    try {
      const returnTo = await completeSignIn(config, window.location.search);
      window.history.replaceState(null, '', returnTo === '/' ? '/projects' : returnTo);
    } catch (err) {
      root.render(
        <Message title="Sign-in failed">
          <p>{(err as Error).message}</p>
          <p className="muted">If you were refused, you may not be in the group allowed to use this site.</p>
          <p><a href="/">Try again</a></p>
        </Message>,
      );
      return;
    }
  }

  if (!getTokens()) {
    await beginSignIn(config, path === '/' ? '/projects' : path);
    return;
  }

  root.render(
    <StrictMode>
      <App config={config} />
    </StrictMode>,
  );
}

void boot();
